/**
 * BroadcastForm - Formulário de envio de broadcast
 */
import React from 'react'
import { Bell, Mail, Copy, ArrowRight, Eye, RotateCcw } from 'lucide-react'
import Card from '../../components/atoms/Card'
import Button from '../../components/atoms/Button'
import Input from '../../components/atoms/Input'
import Select from '../../components/atoms/Select'
import UserMultiSelect from '../../components/molecules/UserMultiSelect'
import MarkdownTextarea from '../../components/molecules/MarkdownTextarea'
import { AUDIENCE_OPTIONS, TYPE_OPTIONS } from '../core/broadcast.constants'

const BroadcastForm = ({
	form,
	onChange,
	onCopy,
	onPreview,
	onReset,
	loading,
}) => {
	const handleChange = (field, value) => onChange(field, value)
	const copyPushToEmail = () => onCopy('pushToEmail')
	const copyEmailToPush = () => onCopy('emailToPush')

	return (
		<div className="space-y-4 md:space-y-6">
			{/* Configurações */}
			<Card className="p-4 md:p-6">
				<h3 className="text-base md:text-lg font-semibold text-text mb-3 md:mb-4">Configurações</h3>
				<div className="space-y-3 md:space-y-4">
					<Select
						label="Tipo de envio"
						value={form.type}
						onChange={(e) => handleChange('type', e.target.value)}
						options={TYPE_OPTIONS}
					/>
					<Select
						label="Audiência"
						value={form.targetAudience}
						onChange={(e) => handleChange('targetAudience', e.target.value)}
						options={AUDIENCE_OPTIONS}
					/>
					{form.targetAudience === 'custom' && (
						<UserMultiSelect
							value={form.customUserIds}
							onChange={(ids) => handleChange('customUserIds', ids)}
							placeholder="Buscar usuários..."
						/>
					)}
				</div>
			</Card>

			{/* Push Content */}
			{(form.type === 'push' || form.type === 'both') && (
				<Card className="p-4 md:p-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 md:mb-4">
						<div className="flex items-center gap-2">
							<Bell className="w-5 h-5 text-blue-500" />
							<h3 className="text-base md:text-lg font-semibold text-text">Notificação Push</h3>
						</div>
						{form.type === 'both' && (
							<Button
								variant="ghost"
								size="sm"
								onClick={copyPushToEmail}
								title="Copiar para Email"
								className="self-start sm:self-auto"
							>
								<Copy className="w-4 h-4 mr-1" />
								<ArrowRight className="w-3 h-3 mr-1" />
								<Mail className="w-4 h-4" />
							</Button>
						)}
					</div>

					<div className="space-y-4">
						<Input
							label="Título"
							value={form.pushTitle}
							onChange={(e) => handleChange('pushTitle', e.target.value)}
							placeholder="Título da notificação (max. 50 caracteres)"
							maxLength={50}
						/>
						<p className="text-xs text-text-muted -mt-3 text-right">
							{form.pushTitle.length}/50
						</p>

						<MarkdownTextarea
							label="Mensagem"
							value={form.pushMessage}
							onChange={(e) => handleChange('pushMessage', e.target.value)}
							placeholder="Mensagem curta e direta. Use **negrito**, *itálico*, listas e mais!"
							maxLength={200}
							rows={4}
							helperText="Suporta formatação Markdown"
						/>
					</div>
				</Card>
			)}

			{/* Email Content */}
			{(form.type === 'email' || form.type === 'both') && (
				<Card className="p-4 md:p-6">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 md:mb-4">
						<div className="flex items-center gap-2">
							<Mail className="w-5 h-5 text-green-500" />
							<h3 className="text-base md:text-lg font-semibold text-text">E-mail</h3>
						</div>
						{form.type === 'both' && (
							<Button
								variant="ghost"
								size="sm"
								onClick={copyEmailToPush}
								title="Copiar para Push"
								className="self-start sm:self-auto"
							>
								<Copy className="w-4 h-4 mr-1" />
								<ArrowRight className="w-3 h-3 mr-1" />
								<Bell className="w-4 h-4" />
							</Button>
						)}
					</div>

					<div className="space-y-4">
						<Input
							label="Assunto"
							value={form.emailSubject}
							onChange={(e) => handleChange('emailSubject', e.target.value)}
							placeholder="Assunto do e-mail (linha de assunto)"
							maxLength={100}
						/>
						<p className="text-xs text-text-muted -mt-3 text-right">
							{form.emailSubject.length}/100
						</p>

						<Input
							label="Título"
							value={form.emailTitle}
							onChange={(e) => handleChange('emailTitle', e.target.value)}
							placeholder="Título principal do e-mail"
							maxLength={100}
						/>
						<p className="text-xs text-text-muted -mt-3 text-right">
							{form.emailTitle.length}/100
						</p>

						<MarkdownTextarea
							label="Corpo do E-mail"
							value={form.emailBody}
							onChange={(e) => handleChange('emailBody', e.target.value)}
							placeholder="Corpo do e-mail com formatação Markdown. Use **negrito**, *itálico*, ### títulos, listas, --- divisores e mais!"
							maxLength={5000}
							rows={10}
							helperText="Suporta formatação Markdown completa"
						/>
					</div>
				</Card>
			)}

			{/* Actions - Sticky no mobile */}
			<div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sticky bottom-0 bg-base-100 py-3 -mx-4 px-4 md:static md:bg-transparent md:py-0 md:mx-0 md:px-0 border-t border-base-200 md:border-0">
				<Button
					variant="outline"
					onClick={onReset}
					disabled={loading}
					className="w-full sm:w-auto order-2 sm:order-1"
				>
					<RotateCcw className="w-4 h-4 mr-2" />
					Limpar
				</Button>
				<Button
					variant="primary"
					onClick={onPreview}
					disabled={loading}
					loading={loading}
					className="w-full sm:w-auto order-1 sm:order-2"
				>
					<Eye className="w-4 h-4 mr-2" />
					<span className="hidden sm:inline">Visualizar e Enviar</span>
					<span className="sm:hidden">Enviar</span>
				</Button>
			</div>
		</div>
	)
}

export default BroadcastForm
